import {
  Body,
  Container,
  Head,
  Heading,
  Html,
  Preview,
  Section,
  Text,
  Hr,
  Link,
} from "@react-email/components";
import * as React from "react";

interface ContactConfirmationEmailProps {
  name: string;
  company: string;
  phone?: string;
  phoneHref?: string;
  email?: string;
  emailHref?: string;
  averageSavings?: string;
}

export const ContactConfirmationEmail = ({
  name,
  company,
  phone = "(512) 690-0075",
  phoneHref = "tel:512-690-0075",
  email = "nick.leija@leijainsurancegroup.com",
  emailHref = "mailto:nick.leija@leijainsurancegroup.com",
  averageSavings = "$300-$500",
}: ContactConfirmationEmailProps) => (
  <Html>
    <Head />
    <Preview>
      Thank you for reaching out to Leija Insurance Group - I'll be in touch
      within 24 hours
    </Preview>
    <Body style={main}>
      <Container style={container}>
        <Section style={logoSection}>
          <Heading style={logo}>Leija Insurance Group</Heading>
          <Text style={tagline}>
            Small Business Benefits That Actually Work
          </Text>
        </Section>

        <Section style={box}>
          <Heading style={heading}>✓ Your Request Was Received</Heading>

          <Text style={paragraph}>Hi {name},</Text>

          <Text style={paragraph}>
            Thank you for reaching out! I've received your inquiry for{" "}
            <strong>{company}</strong> and I'm excited to help you discover how
            we can save you money while improving your employee benefits.
          </Text>

          <Section style={highlightBox}>
            <Text style={highlightText}>
              <strong>⏱️ Response Time:</strong> I'll personally call you within
              24 hours to schedule our discovery meeting.
            </Text>
          </Section>

          <Text style={sectionHeading}>What Happens Next:</Text>

          <Section style={stepBox}>
            <Text style={stepTitle}>Discovery Call</Text>
            <Text style={stepDescription}>
              I'll reach out within 24 hours to schedule a convenient time to
              discuss your specific needs and challenges.
            </Text>
          </Section>

          <Section style={stepBox}>
            <Text style={stepTitle}>Custom Proposals</Text>
            <Text style={stepDescription}>
              You'll receive 3+ tailored proposals with transparent pricing from
              top-rated insurance carriers.
            </Text>
          </Section>

          <Section style={stepBox}>
            <Text style={stepTitle}>Your Decision, Your Time</Text>
            <Text style={stepDescription}>
              No pressure. Take your time to review and decide what's best for
              your business.
            </Text>
          </Section>

          <Hr style={divider} />

          <Text style={contactHeading}>Need to Reach Me?</Text>
          <Text style={paragraph}>
            If you have any urgent questions or prefer to schedule our call
            right away:
          </Text>

          <Section style={contactBox}>
            <Text style={contactItem}>
              📞{" "}
              <Link href={phoneHref} style={link}>
                {phone}
              </Link>
            </Text>
            <Text style={contactItem}>
              ✉️{" "}
              <Link href={emailHref} style={link}>
                {email}
              </Link>
            </Text>
            <Text style={contactNote}>
              Available 24/7 • Evenings & Weekends Welcome
            </Text>
          </Section>

          <Hr style={divider} />

          <Text style={closingText}>
            Looking forward to helping <strong>{company}</strong> save{" "}
            {averageSavings} per employee while providing better benefits to
            your team!
          </Text>

          <Section style={signature}>
            <Text style={signatureName}>Nicholas Leija</Text>
            <Text style={signatureTitle}>Licensed Insurance Agent</Text>
            <Text style={signatureCompany}>Leija Insurance Group</Text>
            <Text style={signatureBadge}>
              🎖️ Military Veteran • Licensed in TX, CA, NV
            </Text>
          </Section>
        </Section>

        <Section style={footer}>
          <Text style={footerText}>
            Serving San Marcos, Kyle, Buda, and the Greater Austin Area
          </Text>
          <Text style={footerCopyright}>
            © 2025 Leija Insurance Group. All rights reserved.
          </Text>
        </Section>
      </Container>
    </Body>
  </Html>
);

ContactConfirmationEmail.PreviewProps = {
  name: "John Smith",
  company: "Acme Corp",
  phone: "(512) 690-0075",
  phoneHref: "tel:512-690-0075",
  email: "nick.leija@leijainsurancegroup.com",
  emailHref: "mailto:nick.leija@leijainsurancegroup.com",
  averageSavings: "$300-$500",
} as ContactConfirmationEmailProps;

export default ContactConfirmationEmail;

const main = {
  backgroundColor: "#f6f9fc",
  fontFamily:
    '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Ubuntu,sans-serif',
  padding: "40px 0",
};

const container = {
  backgroundColor: "#ffffff",
  margin: "0 auto",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
  maxWidth: "600px",
};

const logoSection = {
  backgroundColor: "#1e293b",
  padding: "32px 48px",
  textAlign: "center" as const,
  borderRadius: "8px 8px 0 0",
};

const logo = {
  fontSize: "28px",
  fontWeight: "bold",
  color: "#ffffff",
  margin: "0 0 8px 0",
};

const tagline = {
  fontSize: "14px",
  color: "#cbd5e1",
  margin: "0",
};

const box = {
  padding: "48px",
};

const heading = {
  fontSize: "28px",
  fontWeight: "bold",
  color: "#1e293b",
  marginBottom: "24px",
  marginTop: "0",
};

const paragraph = {
  color: "#475569",
  fontSize: "16px",
  lineHeight: "26px",
  margin: "0 0 16px 0",
};

const sectionHeading = {
  fontSize: "20px",
  fontWeight: "600",
  color: "#1e293b",
  margin: "32px 0 16px 0",
};

const highlightBox = {
  backgroundColor: "#f1f5f9",
  borderLeft: "4px solid #475569",
  padding: "16px 20px",
  margin: "24px 0",
  borderRadius: "4px",
};

const highlightText = {
  color: "#334155",
  fontSize: "15px",
  lineHeight: "24px",
  margin: "0",
};

const stepBox = {
  marginBottom: "24px",
  paddingLeft: "16px",
  borderLeft: "3px solid #e2e8f0",
};

const stepTitle = {
  fontSize: "17px",
  fontWeight: "600",
  color: "#1e293b",
  margin: "0 0 8px 0",
};

const stepDescription = {
  fontSize: "15px",
  color: "#64748b",
  lineHeight: "24px",
  margin: "0",
};

const divider = {
  borderColor: "#e2e8f0",
  margin: "32px 0",
};

const contactHeading = {
  fontSize: "18px",
  fontWeight: "600",
  color: "#1e293b",
  margin: "24px 0 12px 0",
};

const contactBox = {
  backgroundColor: "#f8fafc",
  padding: "24px",
  borderRadius: "6px",
  margin: "16px 0",
};

const contactItem = {
  fontSize: "16px",
  color: "#1e293b",
  margin: "0 0 12px 0",
  lineHeight: "24px",
};

const contactNote = {
  fontSize: "13px",
  color: "#64748b",
  margin: "12px 0 0 0",
  fontStyle: "italic",
};

const link = {
  color: "#475569",
  textDecoration: "underline",
  fontWeight: "500",
};

const closingText = {
  fontSize: "16px",
  color: "#475569",
  lineHeight: "26px",
  margin: "24px 0",
};

const signature = {
  marginTop: "32px",
  paddingTop: "24px",
  borderTop: "2px solid #e2e8f0",
};

const signatureName = {
  fontSize: "18px",
  fontWeight: "600",
  color: "#1e293b",
  margin: "0 0 4px 0",
};

const signatureTitle = {
  fontSize: "14px",
  color: "#64748b",
  margin: "0 0 2px 0",
};

const signatureCompany = {
  fontSize: "14px",
  fontWeight: "500",
  color: "#475569",
  margin: "0 0 8px 0",
};

const signatureBadge = {
  fontSize: "13px",
  color: "#64748b",
  margin: "0",
};

const footer = {
  backgroundColor: "#f8fafc",
  padding: "24px 48px",
  textAlign: "center" as const,
  borderRadius: "0 0 8px 8px",
};

const footerText = {
  fontSize: "13px",
  color: "#64748b",
  margin: "0 0 8px 0",
};

const footerCopyright = {
  fontSize: "12px",
  color: "#94a3b8",
  margin: "0",
};
